import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { MedicusLogo } from '@components/MedicusLogo';
import { TyCMs2Modal } from '@components/onboarding/TyCMs2Modal';
import {
  buscarSocioPorDni,
  getResumenOferta,
  iniciarPagoMs2,
  type CandidatoBusqueda,
  type ResumenOferta,
} from '@lib/services/autogestion-ms2.service';

// Paleta del Figma "step-3-grupo-familiar-ms2" (node 161:1759).
const NAVY = '#002564';
const BLUE = '#6399f8';
const ink = (a: number) => `rgba(0,37,100,${a})`;
const CARD_SHADOW = '0px 1px 3px rgba(0,37,100,0.03), 0px 4px 12px rgba(0,37,100,0.08)';
const BG_GRADIENT = 'linear-gradient(to bottom, #ebf1fb, #f3f6fb 50%, #edf2fa)';
// Avatares/píldoras por integrante (titular → familiares): degradé de azules.
const AVATAR_COLORS = ['#002564', '#1b5cc8', '#4a7fff', '#2f6fe0', '#6a9bff'];

const PILL_PRIMARY: CSSProperties = {
  background: NAVY,
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '16px 24px',
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0px 8px 12px rgba(0,37,100,0.19)',
};
const PILL_GHOST: CSSProperties = {
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${ink(0.15)}`,
  borderRadius: 999,
  padding: '16px 24px',
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
};

function fmtMoney(n: number): string {
  return '$' + Math.round(n).toLocaleString('es-AR');
}

function fmtDni(doc: string): string {
  const d = (doc || '').replace(/\D/g, '');
  return d ? Number(d).toLocaleString('es-AR') : doc;
}

function fmtFecha(s?: string): string {
  if (!s) return '—';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : s;
}

function iniciales(nombres: string, apellido: string): string {
  return `${(nombres[0] ?? '').toUpperCase()}${(apellido[0] ?? '').toUpperCase()}`;
}

function msgError(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { error?: { message?: string }; message?: string } } };
  return err.response?.data?.error?.message ?? err.response?.data?.message ?? fallback;
}

type Paso = 'dni' | 'identidad' | 'oferta';

/**
 * Autogestión PÚBLICA del pase a MS2 por DNI. Flujo: ingreso de DNI →
 * confirmación de identidad (o selección si el DNI está compartido) → oferta
 * MS2 consolidada (grupo + beneficios + costo) → T&C → pasarela de pago.
 * Visual según Figma "step-3-grupo-familiar-ms2" (node 161:1759).
 */
export function MejoraPlanPage() {
  const [paso, setPaso] = useState<Paso>('dni');
  const [dni, setDni] = useState('');
  const [candidatos, setCandidatos] = useState<CandidatoBusqueda[]>([]);
  const [asociadoId, setAsociadoId] = useState<string | null>(null);
  const [tycAbierto, setTycAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Origen de la conversión (deep-link `?origen=`, ej. 'credencial'): se atribuye
  // en el pago. `saltarIdentidad` (ref) = vino deep-linkeado con el DNI ya
  // confirmado en el origen → salta la confirmación de identidad y va a la oferta.
  const [origen, setOrigen] = useState<string | null>(null);
  const saltarIdentidadRef = useRef(false);

  const buscar = useMutation({
    mutationFn: (d: string) => buscarSocioPorDni(d),
    onSuccess: (r) => {
      setError(null);
      if (r.resultado === 'no-encontrado') {
        setError('No encontramos un socio con ese DNI. Revisá el número.');
        return;
      }
      if (r.resultado === 'no-titular') {
        setError(
          'Este DNI no es el titular del grupo familiar. Ingresá con el DNI del titular del plan.',
        );
        return;
      }
      setCandidatos(r.candidatos);
      if (r.resultado === 'unico') {
        setAsociadoId(r.candidatos[0].asociadoId);
        // Deep-link (ya confirmó identidad en el origen) → directo a la oferta.
        setPaso(saltarIdentidadRef.current ? 'oferta' : 'identidad');
      } else {
        setAsociadoId(null);
        setPaso('identidad');
      }
    },
    onError: (e) => setError(msgError(e, 'No se pudo buscar el DNI.')),
  });

  const resumen = useQuery({
    queryKey: ['ms2-resumen', asociadoId],
    queryFn: () => getResumenOferta(asociadoId as string),
    enabled: paso === 'oferta' && !!asociadoId,
    retry: false,
  });

  const pagar = useMutation({
    mutationFn: () => iniciarPagoMs2(asociadoId as string, origen ?? undefined),
    onSuccess: (r) => {
      if (r.urlCheckout) {
        window.location.href = r.urlCheckout;
      } else {
        setTycAbierto(false);
        setError(r.motivoRechazo ?? 'No se pudo iniciar el pago. Reintentá en unos minutos.');
      }
    },
    onError: (e) => {
      setTycAbierto(false);
      setError(msgError(e, 'No se pudo iniciar el pago.'));
    },
  });

  // Deep-link desde otra vista (ej. credencial): `?doc=<dni>` precarga la
  // búsqueda y salta a la oferta; `?origen=` queda para atribuir la conversión
  // en el pago. Corre una sola vez al montar.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const org = sp.get('origen');
    if (org) setOrigen(org);
    const doc = (sp.get('doc') ?? '').replace(/\D/g, '');
    if (doc.length >= 6) {
      saltarIdentidadRef.current = true;
      setDni(doc);
      buscar.mutate(doc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reiniciar() {
    setPaso('dni');
    setDni('');
    setCandidatos([]);
    setAsociadoId(null);
    setError(null);
    saltarIdentidadRef.current = false;
  }

  // En la oferta el contenido es ancho (2 columnas); el resto, centrado angosto.
  const anchoMax = paso === 'oferta' ? 1160 : 460;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: BG_GRADIENT,
      }}
    >
      <header style={{ padding: '20px clamp(20px,5vw,80px)' }}>
        <MedicusLogo />
      </header>

      <main
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          padding: '24px clamp(16px,4vw,40px) 56px',
        }}
      >
        <div style={{ width: '100%', maxWidth: anchoMax }}>
          {error && (
            <div
              style={{
                background: '#fdecec',
                color: '#b3261e',
                border: '1px solid #f5c6c6',
                borderRadius: 12,
                padding: '12px 16px',
                marginBottom: 16,
                fontSize: 14,
                maxWidth: 460,
                marginInline: 'auto',
              }}
            >
              {error}
            </div>
          )}

          {paso === 'dni' && (
            <PasoDni
              dni={dni}
              setDni={setDni}
              cargando={buscar.isPending}
              onContinuar={() => dni.replace(/\D/g, '').length >= 6 && buscar.mutate(dni)}
            />
          )}

          {paso === 'identidad' && (
            <PasoIdentidad
              candidatos={candidatos}
              seleccionado={asociadoId}
              onSeleccionar={setAsociadoId}
              onConfirmar={() => asociadoId && setPaso('oferta')}
              onVolver={reiniciar}
            />
          )}

          {paso === 'oferta' && (
            <PasoOferta
              cargando={resumen.isLoading}
              data={resumen.data}
              error={resumen.isError ? msgError(resumen.error, 'No se pudo cargar la oferta.') : null}
              onContinuar={() => setTycAbierto(true)}
              onVolver={reiniciar}
            />
          )}
        </div>
      </main>

      <footer
        style={{
          padding: '24px clamp(20px,5vw,80px)',
          borderTop: `1px solid ${ink(0.07)}`,
          display: 'flex',
          justifyContent: 'space-between',
          color: ink(0.4),
          fontSize: 14,
        }}
      >
        <span>© Medicus S.A.</span>
        <span>Ayuda y otras consultas</span>
      </footer>

      <TyCMs2Modal
        open={tycAbierto}
        submitting={pagar.isPending}
        onCancelar={() => setTycAbierto(false)}
        onAceptar={() => pagar.mutate()}
      />
    </div>
  );
}

function PasoDni({
  dni,
  setDni,
  cargando,
  onContinuar,
}: {
  dni: string;
  setDni: (v: string) => void;
  cargando: boolean;
  onContinuar: () => void;
}) {
  const habilitado = dni.replace(/\D/g, '').length >= 6;
  return (
    <div style={{ textAlign: 'center', paddingTop: 24 }}>
      <Titulo
        linea1="COTIZÁ EL"
        linea2="PLAN MS2"
        subtitulo="Ingresá tu número de DNI sin puntos ni espacios"
      />
      <div
        style={{
          maxWidth: 420,
          margin: '32px auto 0',
          background: '#fff',
          borderRadius: 16,
          boxShadow: CARD_SHADOW,
          padding: 24,
          textAlign: 'left',
        }}
      >
        <input
          inputMode="numeric"
          value={dni}
          onChange={(e) => setDni(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && onContinuar()}
          placeholder="Ej: 30123456"
          autoFocus
          style={{
            width: '100%',
            padding: '14px 16px',
            borderRadius: 12,
            border: `1px solid ${ink(0.15)}`,
            fontSize: 16,
            color: NAVY,
            outline: 'none',
            background: '#fff',
          }}
        />
        <button
          disabled={cargando || !habilitado}
          onClick={onContinuar}
          style={{
            ...PILL_PRIMARY,
            width: '100%',
            marginTop: 16,
            opacity: cargando || !habilitado ? 0.5 : 1,
          }}
        >
          {cargando ? 'Buscando…' : 'Continuar'}
        </button>
      </div>
    </div>
  );
}

function PasoIdentidad({
  candidatos,
  seleccionado,
  onSeleccionar,
  onConfirmar,
  onVolver,
}: {
  candidatos: CandidatoBusqueda[];
  seleccionado: string | null;
  onSeleccionar: (id: string) => void;
  onConfirmar: () => void;
  onVolver: () => void;
}) {
  if (candidatos.length > 1) {
    return (
      <SeleccionMultiple
        candidatos={candidatos}
        seleccionado={seleccionado}
        onSeleccionar={onSeleccionar}
        onConfirmar={onConfirmar}
        onVolver={onVolver}
      />
    );
  }
  const c = candidatos[0];
  if (!c) return null;
  return <ConfirmacionUnica candidato={c} onConfirmar={onConfirmar} onVolver={onVolver} />;
}

/** Figma "step-2-confirmacion-identidad" (node 136:416): un único titular. */
function ConfirmacionUnica({
  candidato,
  onConfirmar,
  onVolver,
}: {
  candidato: CandidatoBusqueda;
  onConfirmar: () => void;
  onVolver: () => void;
}) {
  return (
    <div style={{ paddingTop: 24, textAlign: 'center' }}>
      <h1
        style={{
          color: NAVY,
          fontSize: 'clamp(28px,5vw,48px)',
          fontWeight: 800,
          lineHeight: 1.05,
          textTransform: 'uppercase',
          margin: 0,
        }}
      >
        Confirmá tus datos para continuar
      </h1>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '32px 0 24px' }}>
        <span style={AVATAR_GRANDE}>{iniciales(candidato.nombres, candidato.apellSolt)}</span>
      </div>
      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          boxShadow: CARD_SHADOW,
          padding: 32,
          textAlign: 'left',
          maxWidth: 500,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 16px' }}>
          <DatoF label="Nombre" valor={`${candidato.nombres} ${candidato.apellSolt}`.trim()} grande />
          <DatoF label="DNI" valor={fmtDni(candidato.documento)} grande />
          <DatoF label="N° de socio" valor={candidato.numeroSocio || '—'} grande />
          <DatoF label="Fecha de nacimiento" valor={fmtFecha(candidato.fechaNacimiento)} grande />
        </div>
        <div style={{ height: 1, background: ink(0.06) }} />
        <DatoF label="Plan actual" valor={candidato.planActual ?? '—'} grande />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 500, margin: '24px auto 0' }}>
        <button style={{ ...PILL_PRIMARY, width: '100%' }} onClick={onConfirmar}>
          Sí, soy yo
        </button>
        <button style={{ ...PILL_GHOST, width: '100%' }} onClick={onVolver}>
          No, no soy yo
        </button>
      </div>
    </div>
  );
}

/** Figma "step-2b-dni-duplicado" (node 147:394): DNI compartido entre titulares. */
function SeleccionMultiple({
  candidatos,
  seleccionado,
  onSeleccionar,
  onConfirmar,
  onVolver,
}: {
  candidatos: CandidatoBusqueda[];
  seleccionado: string | null;
  onSeleccionar: (id: string) => void;
  onConfirmar: () => void;
  onVolver: () => void;
}) {
  return (
    <div style={{ paddingTop: 24 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h1 style={{ color: NAVY, fontSize: 'clamp(28px,5vw,48px)', fontWeight: 800, lineHeight: 1.05, textTransform: 'uppercase', margin: 0 }}>
          Encontramos
        </h1>
        <h1 style={{ color: BLUE, fontSize: 'clamp(28px,5vw,48px)', fontWeight: 800, lineHeight: 1.05, textTransform: 'uppercase', margin: 0 }}>
          Más de un resultado
        </h1>
        <p style={{ color: ink(0.5), fontSize: 15, marginTop: 16 }}>
          Seleccioná la opción que coincida con tus datos personales.
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {candidatos.map((c, idx) => {
          const sel = seleccionado === c.asociadoId;
          const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
          return (
            <button
              key={c.asociadoId}
              onClick={() => onSeleccionar(c.asociadoId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                textAlign: 'left',
                cursor: 'pointer',
                background: '#fff',
                borderRadius: 16,
                boxShadow: CARD_SHADOW,
                border: `${sel ? 2 : 1}px solid ${sel ? BLUE : ink(0.08)}`,
                padding: 24,
              }}
            >
              <RadioDot sel={sel} />
              <span style={avatarStyle(color)}>{iniciales(c.nombres, c.apellSolt)}</span>
              <span style={{ flex: 1 }}>
                <strong style={{ display: 'block', color: NAVY, fontSize: 18 }}>
                  {c.nombres} {c.apellSolt}
                </strong>
                {c.fechaNacimiento && (
                  <small style={{ color: ink(0.5) }}>Fecha de nac.: {fmtFecha(c.fechaNacimiento)}</small>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
        <button
          style={{ ...PILL_PRIMARY, width: '100%', opacity: seleccionado ? 1 : 0.5 }}
          disabled={!seleccionado}
          onClick={onConfirmar}
        >
          Confirmar selección
        </button>
        <button style={{ ...PILL_GHOST, width: '100%' }} onClick={onVolver}>
          Ninguna de estas personas soy yo
        </button>
      </div>
    </div>
  );
}

function RadioDot({ sel }: { sel: boolean }) {
  return (
    <span
      style={{
        width: 24,
        height: 24,
        borderRadius: 12,
        border: `2px solid ${sel ? BLUE : ink(0.24)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {sel && <span style={{ width: 12, height: 12, borderRadius: 6, background: BLUE }} />}
    </span>
  );
}

/**
 * Pantalla de la oferta. Se exporta para poder testearla sola — en particular
 * que NO muestre datos personales del grupo (ver `MejoraPlan.privacidad.spec`).
 */
export function PasoOferta({
  cargando,
  data,
  error,
  onContinuar,
  onVolver,
}: {
  cargando: boolean;
  data?: ResumenOferta;
  error: string | null;
  onContinuar: () => void;
  onVolver: () => void;
}) {
  // VEN-491: la carencia de 12 meses se avisa con un pop-up (pedido de MS, texto
  // validado por marketing). Arranca abierto; al cerrarlo la leyenda queda en la
  // página, así que nadie se queda sin saber por qué no puede continuar.
  const [avisoCerrado, setAvisoCerrado] = useState(false);
  if (cargando) {
    return (
      <p style={{ textAlign: 'center', color: ink(0.5), paddingTop: 40 }}>Cargando tu oferta…</p>
    );
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 24 }}>
        <p style={{ color: '#b3261e' }}>{error}</p>
        <button style={{ ...PILL_GHOST, marginTop: 12 }} onClick={onVolver}>
          Volver
        </button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div>
      <Titulo
        linea1="PROPUESTA"
        linea2="PLAN MS2"
        subtitulo="Beneficios y costo de tu nuevo plan"
        grande
      />

      {/* Una sola columna centrada: beneficios + costo + CTA.
          La lista de integrantes del grupo se quitó el 2026-08-27: esta pantalla
          es PÚBLICA y se entra con un DNI, así que mostrarle a cualquiera el
          nombre, DNI, fecha de nacimiento y N° de socio de toda la familia era
          una exposición de datos de gente que nunca ingresó su documento. Del
          grupo queda SOLO la cantidad de personas, en la tarjeta del costo.
          El backend directamente ya no los manda: no es una cuestión de vista. */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '48px auto 0' }}>
        <div style={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              background: '#f3f6fb',
              border: `1px solid ${ink(0.07)}`,
              borderRadius: 24,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <strong style={{ color: NAVY, fontSize: 18 }}>Beneficios MS2</strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.beneficios.map((b) => (
                <div key={b} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: NAVY,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    <Check size={12} color="#fff" strokeWidth={3} />
                  </span>
                  <span style={{ color: NAVY, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              boxShadow: CARD_SHADOW,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <strong style={{ color: NAVY, fontSize: 18 }}>Costo total del grupo</strong>
              <span
                style={{
                  background: NAVY,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 999,
                  padding: '5px 12px',
                  whiteSpace: 'nowrap',
                }}
              >
                {data.costo.cantidad} {data.costo.cantidad === 1 ? 'persona' : 'personas'}
              </span>
            </div>
            <div style={{ color: NAVY, fontSize: 40, fontWeight: 600, lineHeight: 1 }}>
              {fmtMoney(data.costo.subtotalSinIva)}/mes
            </div>
            <span style={{ color: ink(0.4), fontSize: 13 }}>*Precio mensual total</span>
          </div>

          {data.elegible ? (
            <button style={{ ...PILL_PRIMARY, padding: '18px 24px', fontSize: 17 }} onClick={onContinuar}>
              Continuar
            </button>
          ) : (
            <div
              style={{
                background: '#fff7e6',
                color: '#8a5a00',
                border: '1px solid #f3dca6',
                borderRadius: 12,
                padding: '12px 16px',
                fontSize: 14,
              }}
            >
              <Parrafos texto={data.motivoNoElegible ?? 'No podés pasar a MS2 en este momento.'} />
            </div>
          )}
          {!data.elegible && data.codigoNoElegible === CODIGO_CARENCIA_MS2 && !avisoCerrado && (
            <AvisoCarencia
              texto={data.motivoNoElegible ?? ''}
              onCerrar={() => setAvisoCerrado(true)}
            />
          )}
          <button style={{ ...PILL_GHOST, width: '100%' }} onClick={onVolver}>
            Empezar de nuevo
          </button>
        </div>
      </div>
    </div>
  );
}

/** Código con el que el server marca la carencia de 12 meses del Plan MS2. */
const CODIGO_CARENCIA_MS2 = 'MS2_CARENCIA';

/** Un texto con saltos de línea, como párrafos. */
function Parrafos({ texto }: { texto: string }) {
  const partes = texto.split('\n').filter((t) => t.trim());
  return (
    <>
      {partes.map((t, i) => (
        <p key={t} style={{ margin: i === 0 ? 0 : '10px 0 0' }}>
          {t}
        </p>
      ))}
    </>
  );
}

/** Pop-up de la carencia del Plan MS2 (VEN-491). El texto viene del server. */
function AvisoCarencia({ texto, onCerrar }: { texto: string; onCerrar: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 32, 96, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 50,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="aviso-carencia-titulo"
        style={{
          background: '#fff',
          borderRadius: 20,
          padding: '28px 24px 24px',
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 24px 60px rgba(0, 32, 96, 0.25)',
        }}
      >
        <h2
          id="aviso-carencia-titulo"
          style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#002060' }}
        >
          Todavía no podés cambiar al Plan MS2
        </h2>
        <div style={{ fontSize: 15, lineHeight: 1.5, color: ink(0.8) }}>
          <Parrafos texto={texto} />
        </div>
        <button
          type="button"
          autoFocus
          style={{ ...PILL_PRIMARY, width: '100%', marginTop: 20 }}
          onClick={onCerrar}
        >
          Entendido
        </button>
      </div>
    </div>
  );
}

function DatoF({ label, valor, grande }: { label: string; valor: string; grande?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', color: ink(0.31) }}>
        {label}
      </span>
      <span style={{ fontSize: grande ? 20 : 15, fontWeight: grande ? 700 : 600, color: NAVY }}>{valor}</span>
    </div>
  );
}

function Titulo({
  linea1,
  linea2,
  subtitulo,
  grande,
}: {
  linea1: string;
  linea2: string;
  subtitulo: string;
  grande?: boolean;
}) {
  const size = grande ? 'clamp(40px,7vw,64px)' : 'clamp(30px,6vw,48px)';
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <span style={{ color: NAVY, fontSize: size, fontWeight: 800, lineHeight: 1.05 }}>{linea1}</span>
      <span style={{ color: BLUE, fontSize: size, fontWeight: 800, lineHeight: 1.05 }}>{linea2}</span>
      <span style={{ color: ink(0.4), fontSize: 16, marginTop: 16 }}>{subtitulo}</span>
    </div>
  );
}

function avatarStyle(color: string): CSSProperties {
  return {
    width: 48,
    height: 48,
    borderRadius: 24,
    background: color,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 16,
    flexShrink: 0,
  };
}

// Avatar grande del paso de confirmación de identidad (Figma node 139:25).
const AVATAR_GRANDE: CSSProperties = {
  width: 120,
  height: 120,
  borderRadius: 60,
  background: NAVY,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 38,
  boxShadow: '0px 8px 16px rgba(0,37,100,0.19)',
};
